import { useQuery } from "@tanstack/react-query";
import { api } from "../client";
import type { MetaOutput } from "../../generated/fresh-guard-api";
import { Report } from "../../generated/fresh-guard-api";
import { queryKeys } from "../query-keys.ts";
import { useTranslation } from "react-i18next";
import type {
  ReportingMarketComponentHistoryType,
  ReportingMarketComponentType,
  ReportingUserType,
} from "./Reporting.type.ts";

export type ReportingOrderDTO =
  | import("./Reporting.type.ts").ReportingType
  | null;

export function useReportingSelect(
  metaOutput: MetaOutput | undefined,
  report: Report,
  order: ReportingOrderDTO,
) {
  const { t } = useTranslation();
  const page = metaOutput?.page ?? 1;
  const limit = metaOutput?.limit ?? 10;

  // Normalize order per report type to keep typing tight
  const orderHistory =
    (order as ReportingMarketComponentHistoryType | null) ?? {};
  const orderMarketComponent =
    (order as ReportingMarketComponentType | null) ?? {};
  const orderUser = (order as ReportingUserType | null) ?? {};

  return useQuery({
    queryKey: [
      ...queryKeys.reporting.allPaginated(report, page, limit),
      order ?? {},
    ],
    queryFn: async () => {
      switch (report) {
        case Report.MarketComponentHistory: {
          const res =
            await api.reportingApi.apiReportingmarketComponentHistoryGetCollection(
              page,
              limit,
              orderHistory.orderMarketComponentMarketName,
              orderHistory.orderMarketComponentShortDescription,
              orderHistory.orderMarketComponentNumber,
              orderHistory.orderCreated,
              orderHistory.orderUserUsername,
              orderHistory.orderStatus,
              orderHistory.orderCriticalDate,
            );
          return res.data;
        }
        case Report.MarketComponent306090Verification:
        case Report.MarketComponentWithoutCriticalBbd:
        case Report.MarketComponentWhereShortDescriptionNotEqualsNumber: {
          const res =
            await api.reportingApi.apiReportingmarketComponentReportGetCollection(
              report,
              page,
              limit,
              orderMarketComponent.orderNumber,
              orderMarketComponent.orderCreated,
              orderMarketComponent.orderShortDescription,
              orderMarketComponent.orderDescription,
              orderMarketComponent.orderMarketComponentArticleTypeName,
              orderMarketComponent.orderMarketComponentTypeName,
              orderMarketComponent.orderMarketName,
              orderMarketComponent.orderMarketComponentHistoriesCreated,
            );
          return res.data;
        }
        case Report.User: {
          const res = await api.reportingApi.apiReportinguserGetCollection(
            page,
            limit,
            orderUser.orderUsername,
            orderUser.orderFirstname,
            orderUser.orderLastname,
            orderUser.orderDeactivated,
            orderUser.orderDeactivatedByUsername,
            orderUser.orderCheckCount,
          );
          return res.data;
        }
        default:
          throw new Error(t("error.unknownReportingType"));
      }
    },
    meta: {
      skipGlobalError: false,
    },
  });
}
